/**
 * Loss Run ⇄ `/v2/submissions/{id}/loss-runs/` wire mapping.
 *
 * Field renames (jun-16 backend): operator→operator_name, carrier→
 * carrier_name, retention→sir, paid/reserved→indemnity_paid/expense_paid/
 * indemnity_reserved/expense_reserved, description, valuation_date.
 *
 * The FE row carries BOTH the schedule location's id (`locationId`, the
 * wire `location_id` FK — canonical, since names can be duplicated,
 * OR-1409/OR-1417) and its name (display). Reads resolve id→name against
 * the submission's locations (caller-provided); writes send the staged id
 * directly, with a name→id resolve kept only as a legacy fallback.
 *
 * `sir_by_community` and `total_incurred` are read-only on the FE (SIR by
 * Community / Total Paid & Reserved columns). `sir` (retention at the time
 * of the loss — distinct from `sir_by_community`, the matched location's
 * SIR under the new policy) plus `reopened_date` / `notes` exist on the
 * wire but have no UI yet — left untouched (not sent).
 */
import type {
    LossRunAggregates,
    LossRunListResponseWire,
    LossRunRow,
    LossRunStatus,
    LossRunSummary,
    LossRunSummaryBreakdownWire,
    LossRunSummaryRow,
    LossRunSummaryWire,
    LossRunWire,
    LossRunWritePayload,
    LossRunWriteWire,
} from "../../types/loss-run";
import { emptyToNull, formDateToIso, isoDateToForm, strOrEmpty } from "./submission";

/** Minimal location ref for the `location_id` ⇄ name join. Carries the
 *  facility State so Venue can default to it when none is saved. */
export interface LossRunLocationRef {
    id: string;
    name: string;
    state: string;
}

// `Archived` is included so the flag set by the row menu survives a re-read —
// it isn't a dropdown option, see ARCHIVED_CLAIM_STATUS.
const STATUSES: readonly string[] = ["Open", "Closed", "Re-Open", "Archived"];
function toStatus(raw: string | null): LossRunStatus {
    return STATUSES.includes(raw ?? "") ? (raw as LossRunStatus) : "";
}

export function wireToLossRun(wire: LossRunWire, locations: readonly LossRunLocationRef[]): LossRunRow {
    const loc = wire.location_id ? locations.find((l) => l.id === wire.location_id) : undefined;
    return {
        id: wire.id,
        submissionId: wire.submission_id,
        claimantName: strOrEmpty(wire.claimant),
        claimNumber: strOrEmpty(wire.claim_number),
        operator: strOrEmpty(wire.operator_name),
        location: loc?.name ?? "",
        locationId: strOrEmpty(wire.location_id),
        // Venue defaults to the location's State when none is saved, so a
        // row loaded with a Location but no Venue shows it on first render
        // (not just after a manual re-pick). A saved venue wins.
        venue: strOrEmpty(wire.venue) || (loc?.state ?? ""),
        descriptionOfLoss: strOrEmpty(wire.description),
        incidentDate: isoDateToForm(wire.incident_date),
        reportDate: isoDateToForm(wire.report_date),
        paidIndemnity: strOrEmpty(wire.indemnity_paid),
        paidExpense: strOrEmpty(wire.expense_paid),
        reservedIndemnity: strOrEmpty(wire.indemnity_reserved),
        reservedExpense: strOrEmpty(wire.expense_reserved),
        status: toStatus(wire.status),
        carrier: strOrEmpty(wire.carrier_name),
        valued: isoDateToForm(wire.valuation_date),
        sirByCommunity: strOrEmpty(wire.sir_by_community),
        totalIncurred: strOrEmpty(wire.total_incurred),
        createdAt: wire.created_at,
        updatedAt: wire.updated_at,
    };
}

export function lossRunToWriteWire(payload: LossRunWritePayload, locations: readonly LossRunLocationRef[]): LossRunWriteWire {
    const out: LossRunWriteWire = {};
    if (payload.claimantName !== undefined) out.claimant = emptyToNull(payload.claimantName);
    if (payload.claimNumber !== undefined) out.claim_number = emptyToNull(payload.claimNumber);
    // operator_name is NOT sent (OR-1370/OR-1371): the backend derives it from
    // the linked location via location_id and ignores it on create/update.
    if (payload.locationId !== undefined) {
        // The staged id is the FK itself — no name lookup. Names can be
        // duplicated across schedule locations (OR-1409/OR-1417), so a
        // name-based resolve could pick the wrong row.
        out.location_id = emptyToNull(payload.locationId);
    } else if (payload.location !== undefined) {
        // Name-only fallback (rows staged before the id existed). Empty
        // name clears the FK; an unmatched name (free-typed) also clears
        // it — the location warning surfaces the mismatch.
        out.location_id = locations.find((l) => l.name === payload.location)?.id ?? null;
    }
    if (payload.venue !== undefined) out.venue = emptyToNull(payload.venue);
    if (payload.descriptionOfLoss !== undefined) out.description = emptyToNull(payload.descriptionOfLoss);
    if (payload.incidentDate !== undefined) out.incident_date = formDateToIso(payload.incidentDate);
    if (payload.reportDate !== undefined) out.report_date = formDateToIso(payload.reportDate);
    if (payload.paidIndemnity !== undefined) out.indemnity_paid = emptyToNull(payload.paidIndemnity);
    if (payload.paidExpense !== undefined) out.expense_paid = emptyToNull(payload.paidExpense);
    if (payload.reservedIndemnity !== undefined) out.indemnity_reserved = emptyToNull(payload.reservedIndemnity);
    if (payload.reservedExpense !== undefined) out.expense_reserved = emptyToNull(payload.reservedExpense);
    if (payload.status !== undefined) out.status = emptyToNull(payload.status);
    if (payload.carrier !== undefined) out.carrier_name = emptyToNull(payload.carrier);
    if (payload.valued !== undefined) out.valuation_date = formDateToIso(payload.valued);
    return out;
}

export function wireToAggregates(res: LossRunListResponseWire): LossRunAggregates {
    return {
        totalPaidAndReserved: res.total_paid_and_reserved ?? "",
        lossExcessSir: res.loss_excess_sir ?? "",
        sirAdjustedLossRatio: res.sir_adjusted_loss_ratio ?? "",
    };
}

// ── Summary (Annual Loss Summary + Loss Ratio Analysis tabs) ──────────

export function wireToSummaryRow(w: LossRunSummaryBreakdownWire): LossRunSummaryRow {
    return {
        reportYear: w.report_year,
        indemnityIncurred: strOrEmpty(w.indemnity_incurred),
        expenseIncurred: strOrEmpty(w.expense_incurred),
        reservedIndemnity: strOrEmpty(w.reserved_indemnity),
        reservedExpenses: strOrEmpty(w.reserved_expenses),
        incurred: strOrEmpty(w.incurred),
        incurredClaimsCount: w.incurred_claims_count,
        reportedIncidentsCount: w.reported_incidents_count,
        incurredPriorYear: strOrEmpty(w.incurred_prior_year),
        incurredPercentChange: strOrEmpty(w.incurred_percent_change),
        equivalency: strOrEmpty(w.equivalency),
        onLeveledPremium: strOrEmpty(w.on_leveled_premium),
        onLeveledLossRatio: strOrEmpty(w.on_leveled_loss_ratio),
        selectedPremium: strOrEmpty(w.selected_premium),
        adjustedExcess: strOrEmpty(w.adjusted_excess),
        adjustedLossRatio: strOrEmpty(w.adjusted_loss_ratio),
    };
}

export function wireToSummary(w: LossRunSummaryWire): LossRunSummary {
    return {
        totalPaidAndReserved: strOrEmpty(w.total_paid_and_reserved),
        totalExcess: strOrEmpty(w.total_excess),
        totalAdjustedLossRatio: strOrEmpty(w.total_adjusted_loss_ratio),
        yearsWithLosses: w.years_with_losses ?? null,
        totalEquivalency: strOrEmpty(w.total_equivalency),
        unadjustedRatePerBed: strOrEmpty(w.unadjusted_rate_per_bed),
        premium: strOrEmpty(w.premium),
        totalPremium: strOrEmpty(w.total_premium),
        rows: (w.breakdown ?? []).map(wireToSummaryRow),
    };
}
